/**
 * Learning paths — the types, and the ONE seam between this app and the pathways backend.
 *
 * ---------------------------------------------------------------------------------
 * THIS READS THE REAL BACKEND. There are no fixtures below any more.
 *
 * Three APIs touch pathways on this install and the split matters:
 *
 *   /api/pathways/v1/learner/  OURS, and the only READ this file makes. One request returns
 *                              every pathway visible on this Host with each step's course and
 *                              this learner's completion already resolved.
 *   /api/learning_paths/v1/    OpenCraft's `learning-paths-plugin`. WRITES ONLY here — the two
 *                              enrolment POSTs. Its reads are what this file used to fan out
 *                              over, and no longer calls.
 *   /api/pathways/v1/paths…    thinlms's AUTHORING layer — create, edit, order, publish.
 *                              Tenant-admin only, and it lives in `pathways-admin.ts`.
 *
 * The learner pages must never reach for the authoring API: it answers for a tenant ADMIN,
 * and a page that renders differently for the person who happens to be an administrator is
 * a page nobody can support. `learner/` is `IsAuthenticated` and scoped by Host and session.
 *
 * WHY THE READ MOVED, both measured against plugin 0.4.0 on this install rather than assumed:
 *
 *   1. A STEP IS A BARE `course_key`. To title a card and draw its art, this file resolved
 *      every step against `/api/courses/v1/courses/{id}` — 40 requests on one `/paths` load —
 *      and every enrolled step against `/api/course_home/v1/progress/{id}/`. Neither endpoint
 *      has a bulk form; the join now happens in SQL, server-side, once.
 *   2. THE LIST RESPONSE CARRIED NO DESCRIPTION. `description`, `subtitle`, `level` and
 *      `duration` are detail-only upstream, which is why the detail page fetched a path again.
 *      Our list carries them, so the detail page costs nothing (see `PATH_SEAM_KEY`).
 *
 * ONE UPSTREAM QUIRK IS STILL CORRECTED BELOW, because it is upstream's model order and not
 * its serialiser: `steps` come back in database order, so a path saved 1,2,3 and reordered to
 * 3,2,1 arrives as 3,2,1. Our endpoint orders them, and `assembleView` sorts again anyway —
 * everything that reads a neighbour (sequencing, "next", the artwork) depends on it.
 * ---------------------------------------------------------------------------------
 */

import { openedx, ApiError } from './openedx';
import {
  courseHref,
  courseHeroImage,
  courseImage,
  type CatalogueCourse,
} from './catalogue';

/**
 * OpenCraft's plugin. WRITES ONLY now — the two enrolment POSTs in `joinLearningPath`.
 *
 * Its read endpoints are no longer called from here. They serialise a step as a bare
 * `course_key`, which is what forced this file to resolve every step over the network, one
 * request per course; see `THINLMS_API` below.
 */
const LEARNER_API = '/api/learning_paths/v1';

/**
 * Our own learner read, and the reason this file no longer fans out.
 *
 * `GET /api/pathways/v1/learner/paths` answers the same visibility question as the plugin's
 * list — it is built on the plugin's own `get_paths_visible_to_user` — and additionally
 * resolves each step's COURSE and this learner's COMPLETION in it, server-side, in queries.
 * Measured on the VPS, one `/paths` load: 41 requests became 1.
 */
const THINLMS_API = '/api/pathways/v1';

/* ================================================================ wire types ===== */
/*
 * These mirror what the plugin actually returns, field for field, measured against a live
 * response rather than read off the models — the two differ in exactly the way that costs
 * a day (see the list/detail split above).
 */

/*
 * WHAT HAPPENED TO `grading_criteria`, AND WHY IT IS NOT A TYPE ANY MORE.
 *
 * The plugin stores `required_completion` (0.80 here) and `required_grade` (0.75), and this
 * file used to expose both so the detail page could print "finish 80% of the path" and
 * "reach at least 75% in each course". BOTH LINES WERE FICTION. Nothing on this install
 * compares either number to anything: the plugin's `/progress/` and `/grade/` endpoints
 * report, they do not gate, and no enrolment, unlock or award is conditioned on them
 * anywhere in the stack. A learner sitting on a 0.02 grade was told they had completed the
 * path — which is what a promise nobody enforces looks like from the outside.
 *
 * So the rule this app states is the one it can actually check, approved for this build:
 * A PATH IS COMPLETE WHEN EVERY COURSE IN IT IS COMPLETE. That is derived from the same
 * per-course completion the dashboard and the course pages already measure, so the pathway
 * page cannot disagree with them. The fields are still on the wire type below, unread, so
 * the next person can see they exist and see that nothing consumes them.
 */

export interface LearningPathStep {
  /** An Open edX course key. The step is a reference; the course is the content. */
  course_key: string;
  /** 1-based position. Meaningful only when the path is `sequential`. */
  order: number;
  /** Contribution to path progress. Steps are NOT assumed equal — a capstone is heavier. */
  weight: number;
}

export interface LearningPath {
  key: string;
  /**
   * URL segment.
   *
   * The plugin has no slug, so this IS the key — `pathHref` percent-encodes it, which the
   * key needs anyway because it carries both ':' and '+'. Kept as a separate field rather
   * than having the pages reach for `key` directly, so that a real slug can be introduced
   * later without touching three pages.
   */
  slug: string;
  title: string;
  description: string;
  /** Card artwork — the shape a path takes in a grid. May carry a title in the pixels. */
  image: string | null;
  /**
   * Artwork for a full-bleed heading backdrop, which is NOT the same asset as `image`.
   *
   * A course's card art has its own title typeset into it on this platform's template, so
   * behind an `<h1>` of the same words it reads as a watermark over the heading. This field
   * resolves to a banner or to nothing. See `courseHeroImage` for the measurement.
   */
  heroImage: string | null;
  /**
   * The authors' RECOMMENDED order, not an enforced one.
   *
   * Nothing on the server refuses a learner who opens step 3 first — this app's own
   * sequencing is client-side and a pasted URL walks straight past it. The pages must
   * therefore describe the order as a suggestion; a padlock would be a claim about a
   * restriction that does not exist.
   */
  sequential: boolean;
  /** Not in the public catalogue — the academy adds learners itself. */
  invite_only: boolean;
  steps: LearningPathStep[];
}

export interface LearningPathEnrollment {
  learning_path_key: string;
  /** Upstream's flag. An unenrolled learner is `is_active: false`, not an absent row. */
  is_active: boolean;
}

/**
 * Per-step progress.
 *
 * The plugin has no per-step progress endpoint — its `/progress/` answers for the path as a
 * whole. So a step's progress is the learner's progress in that step's COURSE, and it now
 * arrives WITH the step from `/api/pathways/v1/learner/paths` instead of costing one request
 * each.
 *
 * THE NUMBER IS THE SAME NUMBER. It was `complete_count / (complete_count + incomplete_count)`
 * from `/api/course_home/v1/progress/{id}/`; the server calls the very function that endpoint
 * serialises (`get_course_blocks_completion_summary`) and does the same division. The
 * completion aggregator would have been cheaper and was measured reporting a finished course
 * as half complete on this deployment — see `learner.completion_by_course`.
 *
 * THE GRADE IS GONE, and it is a deletion rather than an omission. `PathStepView.grade` was
 * carried from that endpoint's `course_grade.percent` and NOTHING RENDERED IT — not the
 * card, not the detail page, not the dashboard panel (checked across every consumer). It
 * was the only reason a server-side reader would have had to run `CourseGradeFactory` per
 * step, which `academy.read_grades` measures at 12x the cost of the rest of a page. A field
 * nobody reads is not worth a grading pass per course.
 */
export interface LearningPathStepProgress {
  course_key: string;
  /** 0..1. */
  completion: number;
}

/* ------------------------------------------------------------- the wire shapes -- */

interface WireStep {
  course_key: string;
  order: number;
  weight: number;
  /**
   * The step's course, ALREADY RESOLVED, or null when this academy cannot see it.
   *
   * This is the field that deleted the fan-out. It is the same shape
   * `/api/courses/v1/courses/{id}` returns, field for field, so `courseImage`,
   * `courseHeroImage` and the card components below read it unchanged — see
   * `learner.serialize_course` for why the server emits the platform's serialiser shape
   * rather than its own model's field names.
   */
  course: CatalogueCourse | null;
  /** 0..1, the calling learner's completion in that course. 0 for a path not joined. */
  completion: number;
}

interface WirePath {
  key: string;
  display_name: string;
  image: string | null;
  sequential: boolean;
  invite_only: boolean;
  steps: WireStep[];
  /** On the wire and deliberately unread — see the note above `LearningPathStep`. */
  required_completion: number | null;
  /** The CALLING user's enrolment date, or null. Confirmed per-caller on this install. */
  enrollment_date: string | null;
  /**
   * ALWAYS PRESENT NOW, and that is what removed the detail page's request.
   *
   * Upstream splits these across list and detail — `description`, `subtitle`, `level` and
   * `duration` are detail-only, which is why this file used to fetch one path again to
   * render its page. Our own list carries them, so the detail page reads this list.
   */
  description: string;
  subtitle: string;
}

interface WireList {
  count: number;
  results: WirePath[];
}

/* ================================================================ view types ===== */
/*
 * What the pages actually render. Kept separate from the wire types because every one of
 * these is DERIVED — a step's place in the sequence is not a field the server sends, it is
 * what `sequential` plus the step before it means. Deriving it once here is what stops the
 * list page and the detail page disagreeing about whether a learner has finished a path.
 */

/**
 * `later` IS NOT `locked`, and the rename is the whole point.
 *
 * It means "the course before this one in the recommended order is not finished yet" — a
 * fact about the sequence, not a permission. The state used to be called `locked` and the
 * page drew a padlock and a disabled button for it, which told the learner they were shut
 * out of something they could reach by typing its URL. Nothing server-side enforces the
 * order; this app is the only thing that ever did, and only in its own DOM.
 */
export type StepState = 'completed' | 'in_progress' | 'available' | 'later';

export type PathState = 'not_enrolled' | 'enrolled' | 'in_progress' | 'completed';

export interface PathStepView {
  courseKey: string;
  order: number;
  weight: number;
  title: string;
  summary: string | null;
  image: string | null;
  effort: string | null;
  state: StepState;
  /** 0..1. */
  completion: number;
  /**
   * Whether the step's course resolved in THIS academy's catalogue.
   *
   * A path may reference a course this tenant cannot see — the pathways models hold a
   * course key with no tenancy of their own. An unresolved step keeps its row and loses
   * its link rather than vanishing, because a path that silently drops a step tells the
   * learner the wrong number of courses.
   */
  linked: boolean;
}

export interface LearningPathView {
  path: LearningPath;
  state: PathState;
  steps: PathStepView[];
  stepCount: number;
  stepsCompleted: number;
  /** 0..1, weighted by each step's `weight` — not a plain count of finished steps. */
  progress: number;
  /** Sum of every step's course effort, when the authors filled it in. */
  totalEffort: string | null;
}

/* ================================================================== helpers ===== */

export const pathHref = (slug: string) => `/paths/${encodeURIComponent(slug)}`;

/** Step link target. Unresolved steps have nowhere to go, so callers check `linked`. */
export const pathStepHref = (step: PathStepView) => courseHref(step.courseKey);

/** Percentage for display. Progress is stored 0..1 and shown 0..100. */
export function pct(fraction: number): number {
  return Math.round(fraction * 100);
}

/**
 * FALSE, and it is read rather than deleted on purpose.
 *
 * These pages were fixture-backed before the pathways backend was installed, and both of
 * them show a "sample data" notice gated on this flag. Flipping it here is what removes
 * those notices — the alternative, deleting the banners by hand, is how a product ends up
 * serving fixtures with no notice at all. The flag stays so the mechanism stays provable.
 */
export const PATHS_ARE_FIXTURES = false;

/**
 * TRUE, and it is the single most surprising thing about this backend.
 *
 * Every read endpoint of the pathways plugin is `IsAuthenticated`. Measured on this
 * install: `GET /api/learning_paths/v1/learning-paths/` answers a signed-out visitor with
 * 401 `{"detail":"Authentication credentials were not provided."}` — there is no anonymous
 * catalogue of pathways the way there is one of courses.
 *
 * That matters far beyond an empty page. This app turns a 401 into a WHOLE-APP REDIRECT to
 * /login (see `setUnauthenticatedHandler` in main.tsx), so a visitor who merely opened a
 * course page would have been thrown at a sign-in screen by the "part of these paths" panel
 * on it. So the seam does not make the call at all when nobody is signed in, and the pages
 * read this flag to say why the page is empty rather than claiming the academy has no
 * pathways.
 */
export const PATHS_REQUIRE_SIGN_IN = true;

/* ===================================================================== SEAM ===== */
/*
 * ↓↓↓ THE SEAM. These three functions are the entire surface the pages use. ↓↓↓
 *
 *   listLearningPaths()          GET /api/pathways/v1/learner/paths  — ONE read, and the
 *                                only one. The detail page reads this list too, keyed with
 *                                `PATH_SEAM_KEY`, so there is no per-path fetch any more.
 *   pathContainsCourse()         a PREDICATE over that same list, not a second read. The
 *                                course page filters `PATH_SEAM_KEY` with it in `select`.
 *                                Neither API has a "paths containing course" endpoint, and
 *                                the version of this that fetched one for itself is the
 *                                duplicate `PATH_SEAM_KEY` was written to prevent.
 *   joinLearningPath()           POST /{key}/enrollments/ then POST /{key}/enrollments/
 *                                {course_key}/ per step, both upstream's. THE ONLY WRITE.
 *
 * `getLearningPath(slug)` USED TO BE THE FOURTH and has been deleted rather than left as a
 * one-line wrapper. It existed because upstream's list omits `description` and `subtitle`, so
 * the detail page had to fetch one path again; ours carries them, and a helper that merely
 * searched the list would have been a second place for the "which path is this" rule to live
 * while React Query already holds the list under one key.
 *
 * Tenancy is the SERVER's, not this file's, and it is now the server's TWICE OVER:
 * `openedx_thinlms_pathways.tenancy` scopes which pathways exist for this Host, and
 * `learner.visible_course_map` decides which step courses may be named — through
 * `branding.get_visible_courses`, the platform's own licence-aware visibility question. The
 * browser-side re-check that `getCourse`'s `NotOurCourse` used to provide is gone with the
 * per-course fetch; a step the server declines to resolve arrives as `course: null` and
 * renders as an unlinked row, which is the same outcome by a shorter route.
 */

/**
 * A path with no steps is not a path yet.
 *
 * The authoring API creates the path first and its steps afterwards, so an empty one is a
 * perfectly ordinary intermediate state of somebody's half-finished work — and on a learner
 * surface it rendered as a card reading "0 COURSES", which is an invitation to open a
 * sequence that contains nothing. Filtered HERE, at the seam, rather than in each page: the
 * browse grid, the dashboard library and the course page's "part of" chips would otherwise
 * each need the same rule and would each be a place to forget it.
 *
 * The AUTHORING side (`pathways-admin.ts`) is untouched and still lists it — the author has
 * to be able to see the thing they are half way through building.
 */
const hasSteps = (wire: WirePath) => (wire.steps?.length ?? 0) > 0;

/**
 * THE ONE React Query key for the pathway list, so the four surfaces reading it share a cache.
 *
 * Exported as a function rather than written out at each call site because it is now load-
 * bearing: the browse grid, the dashboard panel, the dashboard library and the pathway DETAIL
 * page all read the same response, and a page that spelled the key differently would silently
 * fetch the whole list again under a private name. That is exactly what the detail page used
 * to do, and it is what `PathDetail` documents removing.
 *
 * `signedIn` is part of the key because the answer genuinely differs: a signed-out visitor
 * gets [] without a request at all (see `PATHS_REQUIRE_SIGN_IN`), and caching that under the
 * same key as a learner's real list would show them an empty catalogue after signing in.
 */
export const PATH_SEAM_KEY = (signedIn: boolean) => ['learning-paths', signedIn] as const;

/** Every path this academy publishes, each already resolved against its own catalogue. */
export async function listLearningPaths(signedIn: boolean): Promise<LearningPathView[]> {
  if (!signedIn) return [];
  return buildViews((await fetchAllPaths()).filter(hasSteps), signedIn);
}


/**
 * Paths that include a given course. A PREDICATE, not a fetch — and that is the change.
 *
 * This is what the course page needs to say "part of". It answers for paths the learner is
 * NOT in as well — a course page that only mentioned pathways you had already joined would
 * hide the one reason to join another.
 *
 * IT USED TO BE `listPathsContainingCourse(courseId, signedIn)`, an async function that
 * called `fetchAllPaths()` itself. That is the failure the note on `PATH_SEAM_KEY` above
 * warns about, committed by the file that wrote the warning: the course page keyed its query
 * `['course-paths', courseId, signedIn]`, so React Query could not know it already held the
 * identical body under `['learning-paths', signedIn]` and fetched the whole academy's
 * pathway catalogue again — once per course page, under a name no other surface shared.
 *
 * MEASURED on the shared dev deployment, 2026-08-18, signed in as a learner: 20
 * paths across 45 distinct step courses in one 68.7 KB body, downloaded five times to browse
 * five course pages and render two chips. (The `fetchAllPaths` docstring's "17 paths, 40
 * courses, 30.9 KB" was measured on the VPS when it was written; the payload has more than
 * doubled since, which is the point — a per-page copy of it only gets worse.)
 *
 * As a predicate the course page reads `PATH_SEAM_KEY` like every other surface and derives
 * its chips with `select`, so the catalogue is fetched once for the session and the filtering
 * costs nothing. `courseKey` here is `PathStepView`'s camelCase spelling of the wire's
 * `course_key`; `assembleView` carries the value through unchanged.
 */
export const pathContainsCourse = (view: LearningPathView, courseId: string): boolean =>
  view.steps.some((step) => step.courseKey === courseId);

/* ================================================================ joining ===== */

/** One step, named the way a learner would recognise it, plus why it went the way it did. */
export interface PathJoinStep {
  courseKey: string;
  /** The course's real title, or its position when this academy cannot resolve it. */
  title: string;
  /** Present on failures and skips only. */
  detail?: string;
}

/**
 * What happened, step by step. Never a bare boolean — see `joinLearningPath`.
 */
export interface PathJoinResult {
  /** The learner is in the PATH. False is impossible here: a failure there throws. */
  joined: boolean;
  /** Course enrolments that went through, or that were already in place. */
  enrolled: PathJoinStep[];
  /** Course enrolments that were attempted and refused. The honest half of the answer. */
  failed: PathJoinStep[];
  /** Steps never attempted, because their course does not exist in this academy. */
  skipped: PathJoinStep[];
}

/** True when every step this academy can see was enrolled. */
export const joinedCleanly = (result: PathJoinResult) => result.failed.length === 0;

/**
 * Join a learning path, and then join its courses.
 *
 * ---------------------------------------------------------------------------------
 * THIS IS THE FUNCTION THE WHOLE FEATURE WAS MISSING. Every progress element on every
 * pathway surface is gated on `enrollment_date`, and nothing in this app had ever written
 * one. The backend was measured tracking 0 → 50 → 100% correctly the whole time; a learner
 * could finish every course in a path and the path page would not move, because as far as
 * the server was concerned they had never joined it.
 *
 * BOTH CALLS ARE OPENCRAFT'S OWN. Nothing here is a thinlms endpoint:
 *
 *   POST /api/learning_paths/v1/{key}/enrollments/          the path
 *   POST /api/learning_paths/v1/{key}/enrollments/{course}/ one step's course
 *
 * The first is `IsAuthenticated + IsAdminOrSelf`, and `IsAdminOrSelf` passes a non-staff
 * caller who names nobody — so a learner enrolling THEMSELVES is an allowed request, by
 * design, with an empty body. The second requires the caller to already hold a path
 * enrolment (`filter(enrollment_date__isnull=False)`), which is why the order below is not
 * negotiable and why the path call is awaited rather than raced with the course calls.
 *
 * WHY THE COURSES ARE A SEPARATE LOOP AT ALL. Upstream's path enrolment does not touch
 * course enrolments — that is its documented design, not an oversight — so a learner who
 * joined only the path would see a pathway page full of courses they still could not open.
 *
 * ---------------------------------------------------------------------------------
 * PARTIAL FAILURE, WHICH IS THE PART NOBODY HAS SEEN GO WRONG YET.
 *
 * This is N sequential writes over a network. The honest shapes of failure are: the path
 * call fails (nothing else is attempted, and this function THROWS so the caller shows a
 * plain error); or the path call succeeds and some course calls do not. The second is the
 * dangerous one, because it looks like success from the outside — the learner IS in the
 * path, the page WILL flip to the enrolled state, and two of their five courses are missing.
 *
 * So: every course failure is caught, named, and RETURNED. Nothing is swallowed, no failure
 * is inferred from a count, and the loop does not abort on the first refusal — stopping
 * early would leave a learner short of courses that would have enrolled fine, and would
 * report fewer failures than there are.
 *
 * RETRY IS SAFE, and that is a property of upstream's own code rather than a hope:
 *   - the path call is `get_or_create`; a second attempt answers 409 "Enrollment exists.",
 *     which is treated below as the success it describes;
 *   - the course call is `CourseEnrollment.enroll`, which is idempotent for an already
 *     enrolled learner.
 * Re-running therefore cannot double-enrol anyone or move any state backwards, which is
 * what lets the UI offer "Try the rest again" instead of an apology.
 *
 * SEQUENTIAL, NOT `Promise.all`. Each call is a Django write that fans out to enrolment
 * signals; firing five at once at a single-worker LMS is how the fifth times out and gets
 * reported as a failure that would have succeeded on its own.
 */
export async function joinLearningPath(view: LearningPathView): Promise<PathJoinResult> {
  const key = view.path.key;

  // ---- 1. the path itself. A failure here is total, so it is thrown, not collected.
  try {
    await openedx.post(`${LEARNER_API}/${encodeURIComponent(key)}/enrollments/`, {});
  } catch (error) {
    // 409 is upstream's answer for "you are already enrolled" — the state this call exists
    // to reach. Treating it as an error would make the retry button permanently broken for
    // exactly the learner who needs it: the one whose courses half-failed last time.
    if (!(error instanceof ApiError && error.status === 409)) throw error;
  }

  const result: PathJoinResult = { joined: true, enrolled: [], failed: [], skipped: [] };

  // ---- 2. the courses, in the authors' order, one at a time.
  for (const step of [...view.steps].sort((a, b) => a.order - b.order)) {
    const named: PathJoinStep = { courseKey: step.courseKey, title: step.title };

    if (!step.linked) {
      // Not attempted on purpose. The step's course is outside this academy's catalogue,
      // and asking the LMS to enrol somebody into another tenant's course is not a request
      // this app should make even where the server would allow it.
      result.skipped.push({ ...named, detail: 'This course is not available in this academy.' });
      continue;
    }

    try {
      await openedx.post(
        `${LEARNER_API}/${encodeURIComponent(key)}/enrollments/${encodeURIComponent(step.courseKey)}/`,
        {},
      );
      result.enrolled.push(named);
    } catch (error) {
      result.failed.push({ ...named, detail: describeJoinFailure(error) });
    }
  }

  return result;
}

/**
 * A sentence a learner can act on, from whatever the LMS said.
 *
 * THE SERVER'S OWN SENTENCE WINS, WHATEVER THE STATUS. This function used to read `detail`
 * on a 400 only, and on a 403 it substituted a sentence of its own. That threw away the one
 * message on this route that a learner most needs: the paywall refuses a course an academy
 * sells with a 403 whose body says what it costs and how to buy it — "This course must be
 * purchased before you can enrol. It costs MYR 53.00. ..." — and this function replaced it
 * with "This course is not open for enrolment.", which reads as the course being closed and
 * sends the learner to support instead of to checkout.
 *
 * So a `detail` is preferred at every status, and the sentences below are what is shown when
 * the server did not write one. They are kept per status rather than collapsed into one line
 * because they are all upstream's own answers and they mean different things: 400 when
 * `enroll_user_in_course` returns False for a reason it did not explain or the course is not
 * a step, 403 when the course refuses this learner, 404 when the path enrolment the previous
 * call created is not visible to this request. Anything else keeps its number rather than
 * being flattened into "something went wrong" — a support conversation needs it.
 */
export function describeJoinFailure(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'The academy could not be reached.';
  }
  const detail = (error.body as { detail?: string } | null)?.detail;
  if (detail) return detail;
  if (error.status === 403) return 'This course is not open for enrolment.';
  if (error.status === 404) return 'This course is no longer part of the path.';
  if (error.status === 400) return 'The academy refused the enrolment.';
  return `The academy answered ${error.status}.`;
}

/* =============================================================== fetch layer ===== */

/**
 * THE WHOLE FETCH LAYER. One request, and it used to be forty-one.
 *
 * What it replaces, measured on the VPS as one `/paths` load for a signed-in learner:
 *
 *     GET /api/learning_paths/v1/learning-paths/?page=1&page_size=100     1
 *     GET /api/learning_paths/v1/learning-paths/?page=2&page_size=100     1
 *     GET /api/courses/v1/courses/{key}                                  40   one per step course
 *     GET /api/course_home/v1/progress/{key}/                             N   one per enrolled step
 *
 * The plugin's list paginates and returns a step as a bare `course_key`, so this file asked
 * for every page and then resolved every key. Our endpoint does the join in SQL and returns
 * the whole catalogue in one body: 17 paths, 40 distinct courses, 30.9 KB.
 *
 * NO PAGINATION HERE, and it is not an oversight. The browse page renders every path at once
 * and searches them client-side, so a page boundary would be a path the learner cannot find
 * by scrolling OR by searching. The response carries `count`; if an academy ever publishes
 * enough pathways to need paging, that number is what will say so.
 */
async function fetchAllPaths(): Promise<WirePath[]> {
  const response = await openedx.get<WireList>(`${THINLMS_API}/learner/paths`);
  return response.results ?? [];
}

/* ============================================================== view assembly ===== */

/**
 * Wire records to views. PURE, and that is the change worth noticing.
 *
 * This function used to be `async` and to make two rounds of network calls of its own — the
 * course resolution and the progress reads. Both arrive with the steps now, so assembling a
 * view is arithmetic over data already in hand. It matters beyond tidiness: every surface
 * goes through here, and while it made requests, opening a pathway page or a course page
 * re-fetched every course in the academy's pathways again.
 */
function buildViews(wirePaths: WirePath[], signedIn: boolean): LearningPathView[] {
  return wirePaths.map((wire) => {
    const courses = new Map<string, CatalogueCourse>();
    for (const step of wire.steps) {
      if (step.course) courses.set(step.course.id, step.course);
    }

    // A signed-out visitor is never enrolled in anything, whatever the server says about a
    // stale row. The browse state is the one a visitor deciding whether to join actually
    // sees, so it has to be the one we render for them. (The endpoint is `IsAuthenticated`,
    // so in practice this only fires for the brief window before the session resolves.)
    const enrolled = signedIn && wire.enrollment_date !== null;

    const progressRows: LearningPathStepProgress[] = enrolled
      ? wire.steps.map((step) => ({ course_key: step.course_key, completion: step.completion }))
      : [];

    return assembleView(toPath(wire, courses), enrolled, progressRows, courses);
  });
}

/** Wire record to the shape the pages read. */
function toPath(wire: WirePath, courses: Map<string, CatalogueCourse>): LearningPath {
  const steps: LearningPathStep[] = wire.steps.map((step) => ({
    course_key: step.course_key,
    order: step.order,
    weight: step.weight,
  }));

  return {
    key: wire.key,
    slug: wire.key,
    title: wire.display_name,
    // Subtitle is the fallback because the list response carries neither and the detail
    // response may carry only one; an empty string renders as nothing, which is correct.
    description: wire.description || wire.subtitle || '',
    // A path has no artwork of its own until an author uploads one, and the model has no
    // upload yet — so the first step's course image stands in, the same treatment Discover
    // gives a course with no image of its own.
    image: wire.image ?? firstImage(steps, courses, courseImage),
    // The SAME rule against a different field, so a path's hero borrows its first course's
    // BANNER rather than its card. A path whose courses have no banner gets nothing, and
    // the hero keeps its brand gradient — see `courseHeroImage`.
    heroImage: wire.image ?? firstImage(steps, courses, courseHeroImage),
    sequential: wire.sequential,
    invite_only: wire.invite_only,
    steps,
  };
}

function firstImage(
  steps: LearningPathStep[],
  courses: Map<string, CatalogueCourse>,
  pick: (media: CatalogueCourse['media']) => string | null,
): string | null {
  // In `order`, not in the order the server happened to return them — the artwork of a
  // path is its FIRST course's, and the list arrives unsorted.
  for (const step of [...steps].sort((a, b) => a.order - b.order)) {
    const course = courses.get(step.course_key);
    const image = course ? pick(course.media) : null;
    if (image) return image;
  }
  return null;
}

/**
 * Turn a path plus its progress into the view. Pure, and deliberately so — this is the
 * function everything above feeds, and the only place that decides what "later in the
 * sequence" and "completed" mean.
 */
function assembleView(
  path: LearningPath,
  enrolled: boolean,
  progressRows: LearningPathStepProgress[],
  courses: Map<string, CatalogueCourse>,
): LearningPathView {
  // THE UPSTREAM QUIRK, handled once. Steps arrive in database order, so everything below
  // that reads a neighbour — sequential locking, the "next" step, the artwork — would be
  // wrong without this line.
  const ordered = [...path.steps].sort((a, b) => a.order - b.order);

  const steps: PathStepView[] = ordered.map((step, index) => {
    const course = courses.get(step.course_key) ?? null;
    const progress = progressRows.find((row) => row.course_key === step.course_key);
    const completion = progress?.completion ?? 0;

    // The step before this one, already decided. Sequencing reads the PREVIOUS step's
    // completion rather than a server flag, so an unenrolled visitor browsing the path
    // sees the shape of it without every row telling them it comes later.
    const previous = index === 0 ? undefined : ordered[index - 1];
    const previousDone =
      previous === undefined ||
      (progressRows.find((row) => row.course_key === previous.course_key)?.completion ?? 0) >= 1;

    let state: StepState;
    if (completion >= 1) state = 'completed';
    else if (completion > 0) state = 'in_progress';
    else if (enrolled && path.sequential && !previousDone) state = 'later';
    else state = 'available';

    return {
      courseKey: step.course_key,
      order: step.order,
      weight: step.weight,
      // The real course's name wins. A step whose course this academy cannot see keeps its
      // row and is labelled by its position — it still has to say what it is.
      title: course?.name ?? `Step ${step.order}`,
      summary: course?.short_description ?? null,
      image: course ? courseImage(course.media) : null,
      effort: course?.effort ?? null,
      state,
      completion,
      linked: course !== null,
    };
  });

  const totalWeight = steps.reduce((sum, step) => sum + step.weight, 0);
  const earnedWeight = steps.reduce((sum, step) => sum + step.weight * step.completion, 0);
  const progress = totalWeight > 0 ? earnedWeight / totalWeight : 0;
  const stepsCompleted = steps.filter((step) => step.state === 'completed').length;

  let state: PathState;
  if (!enrolled) state = 'not_enrolled';
  // EVERY course, not a share of them. This used to be `stepsCompleted >= ceil(0.80 × N)`,
  // which on a two-course path declared the whole thing complete after one course and did
  // it while the second course's grade sat at 0.02. `required_completion` is a stored
  // number no part of this platform enforces; finishing the courses is a measurement.
  else if (steps.length > 0 && stepsCompleted === steps.length) state = 'completed';
  else if (progress > 0) state = 'in_progress';
  else state = 'enrolled';

  return {
    path,
    state,
    steps,
    stepCount: steps.length,
    stepsCompleted,
    progress,
    // Only stated when EVERY step's author filled in `effort` — a total built from three
    // of five courses is not the length of the path, and printing it as though it were is
    // how a learner ends up planning around half a number.
    totalEffort: steps.every((step) => step.effort) ? summariseEffort(steps) : null,
  };
}

/**
 * "4 hours per week" × 5 courses is not a sum this app can do — `effort` is free text as
 * typed in Studio, with no agreed unit. So the row states the count it is confident about
 * and leaves the arithmetic alone.
 */
function summariseEffort(steps: PathStepView[]): string {
  return `${steps.length} ${steps.length === 1 ? 'course' : 'courses'} · effort set by each course team`;
}
